// Roles & People — role catalogue + per-user permission overrides.
//
// One operator (admin-gated) surface, routed by four sidebar entries:
// Security → People, Security → Roles, Talent → Role Assignments, and
// Talent → Permissions. All render this component.
//
// Wired to /api/square/command-center/permissions (GET) → { roles,
// overrides }, plus the POST route via api.commandCenter.permissions.set
// to grant/deny a per-user override. Every route is server-side,
// admin-gated (minLevel 80), and fails closed. The write links an
// Olivium receipt server-side — this client never fabricates outcomes.
//
// Non-happy states handled explicitly: loading, auth-required (sign in),
// forbidden (operator access required), and any other error (honest
// warning banner).

import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  DataGrid,
  Banner,
  InlineAlert,
  StatusBadge,
  Modal,
  FormField,
  Input,
  Toggle,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from '@square/ui'
import {
  api,
  ApiError,
  isAuthError,
  isForbiddenError,
  type CommandCenterPermissionOverride,
} from '../../../api/client'
import EmptyState from '../../../components/EmptyState'
import RouteHeader from '../../RouteHeader'

// Role catalogue row as returned by permissions.list().
interface RoleRow {
  id: string
  name: string
  level: number
  permissions: string[]
  description: string | null
}

// The one role we annotate inline in the catalogue. This is a display
// note only — the level is authoritative from the server, never invented.
const BACKEND_MANAGER_ROLE = 'backend_manager'
const BACKEND_MANAGER_LEVEL = 70

function formatStamp(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd} ${hh}:${mi}`
}

// Shorten a long opaque identifier (user_id, org_id) for mono columns.
// Full value stays available on hover title.
function truncateId(value: string | null | undefined, head = 8, tail = 6): string {
  if (!value) return '—'
  if (value.length <= head + tail + 1) return value
  return `${value.slice(0, head)}…${value.slice(-tail)}`
}

const monoCell: React.CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
}

interface OverrideForm {
  user_id: string
  org_id: string
  permission: string
  granted: boolean
  resource_type: string
  reason: string
}

const EMPTY_FORM: OverrideForm = {
  user_id: '',
  org_id: '',
  permission: '',
  granted: true,
  resource_type: '',
  reason: '',
}

export default function RolesPeople() {
  const [roles, setRoles] = useState<RoleRow[]>([])
  const [overrides, setOverrides] = useState<CommandCenterPermissionOverride[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [forbidden, setForbidden] = useState(false)

  // Add-override modal state.
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState<OverrideForm>(EMPTY_FORM)
  const [busy, setBusy] = useState(false)

  function formatError(err: unknown): string {
    if (err instanceof ApiError) {
      if (!err.message || err.message === err.code) return err.code
      return `${err.code}: ${err.message}`
    }
    if (err instanceof Error) return err.message || 'request_failed'
    return String(err)
  }

  // Route any thrown error into the correct honest state. Returns true
  // when it was an auth/forbidden case the caller should stop on.
  const routeError = useCallback((err: unknown): boolean => {
    if (isAuthError(err)) {
      setAuthRequired(true)
      setForbidden(false)
      setError(null)
      setRoles([])
      setOverrides([])
      return true
    }
    if (isForbiddenError(err) || (err instanceof ApiError && err.code === 'forbidden')) {
      setForbidden(true)
      setAuthRequired(false)
      setError(null)
      setRoles([])
      setOverrides([])
      return true
    }
    setError(formatError(err))
    return false
  }, [])

  const fetchPermissions = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAuthRequired(false)
    setForbidden(false)
    try {
      const result = await api.commandCenter.permissions.list()
      setRoles(result.roles || [])
      setOverrides(result.overrides || [])
    } catch (err) {
      routeError(err)
    } finally {
      setLoading(false)
    }
  }, [routeError])

  useEffect(() => { void fetchPermissions() }, [fetchPermissions])

  function openAdd() {
    setForm(EMPTY_FORM)
    setError(null)
    setAddOpen(true)
  }

  function closeAdd() {
    setAddOpen(false)
    setForm(EMPTY_FORM)
  }

  function patchForm<K extends keyof OverrideForm>(key: K, value: OverrideForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  // The three required fields for a well-formed override.
  const formValid =
    form.user_id.trim().length > 0 &&
    form.org_id.trim().length > 0 &&
    form.permission.trim().length > 0

  async function onSubmit() {
    if (!formValid) return // guarded by the disabled button, defensive here too
    setBusy(true)
    setError(null)
    try {
      await api.commandCenter.permissions.set({
        user_id: form.user_id.trim(),
        org_id: form.org_id.trim(),
        permission: form.permission.trim(),
        granted: form.granted,
        resource_type: form.resource_type.trim() ? form.resource_type.trim() : null,
        reason: form.reason.trim() || undefined,
      })
      closeAdd()
      await fetchPermissions()
    } catch (err) {
      const handled = routeError(err)
      if (handled) closeAdd()
    } finally {
      setBusy(false)
    }
  }

  const gated = authRequired || forbidden

  return (
    <div>
      <RouteHeader
        kicker="SECURITY · ROLES & PEOPLE"
        title="Roles & People"
        description="The role catalogue and per-user permission overrides. Grant or deny a scoped permission under an operator reason — every write is linked to an Olivium receipt."
        crumbs={[
          { label: 'Command Center', href: '/command-center/overview' },
          { label: 'Roles & People' },
        ]}
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => void fetchPermissions()} loading={loading}>
              Refresh
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={openAdd}
              disabled={loading || gated}
            >
              Add permission override
            </Button>
          </>
        }
      />

      {error && (
        <div style={{ marginBottom: 14 }}>
          <Banner severity="warning" title="Roles & People">{error}</Banner>
        </div>
      )}

      {authRequired ? (
        <EmptyState
          title="Sign in to continue"
          body="Roles & People reads the live role catalogue and permission overrides and requires an authenticated operator session. Sign in at /auth/login to load and manage them."
        />
      ) : forbidden ? (
        <EmptyState
          title="Operator access required"
          body="This is an admin-gated surface. The role catalogue and per-user permission overrides are restricted to the Master Architect and authorized operators. Your account does not have operator access."
        />
      ) : loading ? (
        <InlineAlert severity="info">Loading roles and overrides…</InlineAlert>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {/* ── Section 1 · Role catalogue ── */}
          <section>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 10,
              }}
            >
              <h2
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  margin: 0,
                  color: 'var(--olv-text-primary)',
                }}
              >
                Role catalogue
              </h2>
              <StatusBadge tone="info">{roles.length} roles</StatusBadge>
            </div>

            <div
              style={{
                fontSize: 12,
                color: 'var(--olv-text-secondary)',
                lineHeight: 1.6,
                marginBottom: 12,
              }}
            >
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--olv-text-primary)' }}>
                {BACKEND_MANAGER_ROLE}
              </span>{' '}
              (level {BACKEND_MANAGER_LEVEL}) is the Backend / Database Warehouse Manager role, scoped
              per project / module. It is not a global operator role — its authority is bounded to the
              backend and database warehouse of the project or module it is granted against.
            </div>

            {roles.length === 0 ? (
              <EmptyState
                title="No roles in the catalogue"
                body="The role catalogue is empty. Roles are provisioned server-side; none are defined yet."
              />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <THead>
                    <TR>
                      <TH width={180}>NAME</TH>
                      <TH width={70} align="right">LEVEL</TH>
                      <TH>PERMISSIONS</TH>
                      <TH>DESCRIPTION</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {roles.map((role) => {
                      const isBackendManager =
                        role.name === BACKEND_MANAGER_ROLE || role.level === BACKEND_MANAGER_LEVEL
                      return (
                        <TR key={role.id}>
                          <TD mono>
                            <span style={{ color: 'var(--olv-text-primary)' }}>{role.name}</span>
                            {isBackendManager && (
                              <span
                                style={{
                                  display: 'block',
                                  fontSize: 10,
                                  color: 'var(--olv-text-tertiary)',
                                  marginTop: 2,
                                }}
                              >
                                Backend / DB Warehouse Manager · scoped per project / module
                              </span>
                            )}
                          </TD>
                          <TD mono align="right">{role.level}</TD>
                          <TD mono>
                            {role.permissions && role.permissions.length > 0
                              ? role.permissions.join(', ')
                              : '—'}
                          </TD>
                          <TD>{role.description || '—'}</TD>
                        </TR>
                      )
                    })}
                  </TBody>
                </Table>
              </div>
            )}
          </section>

          {/* ── Section 2 · Permission overrides ── */}
          <section>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 10,
              }}
            >
              <h2
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  margin: 0,
                  color: 'var(--olv-text-primary)',
                }}
              >
                Permission overrides
              </h2>
              <StatusBadge tone="info">{overrides.length} overrides</StatusBadge>
            </div>

            <div
              style={{
                fontSize: 12,
                color: 'var(--olv-text-secondary)',
                lineHeight: 1.6,
                marginBottom: 12,
              }}
            >
              Per-user grants and denials layered on top of the role catalogue. A deny always beats a
              grant. Each override is linked to an Olivium receipt.
            </div>

            <DataGrid
              columns={[
                {
                  id: 'user_id',
                  header: 'USER',
                  mono: true,
                  width: 140,
                  render: (r: CommandCenterPermissionOverride) => (
                    <span style={monoCell} title={r.user_id}>{truncateId(r.user_id)}</span>
                  ),
                },
                {
                  id: 'org_id',
                  header: 'ORG',
                  mono: true,
                  width: 140,
                  render: (r: CommandCenterPermissionOverride) => (
                    <span style={monoCell} title={r.org_id}>{truncateId(r.org_id)}</span>
                  ),
                },
                {
                  id: 'permission',
                  header: 'PERMISSION',
                  mono: true,
                  width: 180,
                  render: (r: CommandCenterPermissionOverride) => (
                    <span style={monoCell} title={r.permission}>{r.permission}</span>
                  ),
                },
                {
                  id: 'granted',
                  header: 'DECISION',
                  width: 100,
                  render: (r: CommandCenterPermissionOverride) => (
                    <StatusBadge tone={r.granted ? 'live' : 'critical'}>
                      {r.granted ? 'grant' : 'deny'}
                    </StatusBadge>
                  ),
                },
                {
                  id: 'resource_type',
                  header: 'RESOURCE',
                  mono: true,
                  width: 130,
                  render: (r: CommandCenterPermissionOverride) => r.resource_type || '—',
                },
                {
                  id: 'reason',
                  header: 'REASON',
                  render: (r: CommandCenterPermissionOverride) => r.reason || '—',
                },
                {
                  id: 'created_at',
                  header: 'CREATED',
                  mono: true,
                  width: 150,
                  render: (r: CommandCenterPermissionOverride) => formatStamp(r.created_at),
                },
              ]}
              rows={overrides}
              getRowId={(r: CommandCenterPermissionOverride) => r.id}
              emptyMessage="No permission overrides. Every user resolves to their role's permissions."
            />
          </section>
        </div>
      )}

      {/* Add permission override — user_id, org_id, permission required. */}
      <Modal
        open={addOpen}
        onClose={busy ? undefined : closeAdd}
        title="Add permission override"
        width={480}
        footer={
          <>
            <Button variant="ghost" onClick={closeAdd} disabled={busy}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => void onSubmit()}
              loading={busy}
              disabled={busy || !formValid}
            >
              {form.granted ? 'Grant' : 'Deny'} permission
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField
            label="User ID"
            required
            help="The user this override applies to (Supabase user id)."
          >
            <Input
              value={form.user_id}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => patchForm('user_id', e.target.value)}
              placeholder="e.g. 8f2c…"
              disabled={busy}
            />
          </FormField>

          <FormField
            label="Org ID"
            required
            help="The organization scope for this override."
          >
            <Input
              value={form.org_id}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => patchForm('org_id', e.target.value)}
              placeholder="e.g. org_…"
              disabled={busy}
            />
          </FormField>

          <FormField
            label="Permission"
            required
            help="The permission key to grant or deny (e.g. projects.write)."
          >
            <Input
              value={form.permission}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => patchForm('permission', e.target.value)}
              placeholder="e.g. projects.write"
              disabled={busy}
            />
          </FormField>

          <FormField
            label="Decision"
            help="Grant allows the permission; deny explicitly blocks it. A deny always beats a grant."
          >
            <Toggle
              checked={form.granted}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => patchForm('granted', e.target.checked)}
              label={form.granted ? 'Grant' : 'Deny'}
              disabled={busy}
            />
          </FormField>

          <FormField
            label="Resource type"
            help="Optional. Scopes the override to a resource kind (e.g. project, site)."
          >
            <Input
              value={form.resource_type}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => patchForm('resource_type', e.target.value)}
              placeholder="optional — e.g. project"
              disabled={busy}
            />
          </FormField>

          <FormField
            label="Reason"
            help="Optional. Recorded on the Olivium receipt for this override."
          >
            <Input
              value={form.reason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => patchForm('reason', e.target.value)}
              placeholder="optional — e.g. temporary elevation for migration work"
              disabled={busy}
            />
          </FormField>
        </div>
      </Modal>
    </div>
  )
}
